import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthProvider';
import { adminDashboardStats } from '../lib/api';

interface DashboardStats {
  users: {
    total: number;
    today: number;
    yesterday: number;
    active24h: number;
    growthRate: string;
    suspended: number;
    shadowBanned: number;
  };
  posts: {
    total: number;
    today: number;
    yesterday: number;
    growthRate: string;
  };
  engagement: {
    totalComments: number;
    totalLikes: number;
    avgLikesPerPost: string;
    avgCommentsPerPost: string;
  };
  demographics?: {
    ageGroups: {
      '13-17': number;
      '18-24': number;
      '25-34': number;
      '35-44': number;
      '45-54': number;
      '55+': number;
      'unknown': number;
    };
    genderDistribution: {
      male: number;
      female: number;
      other: number;
      'prefer-not-to-say': number;
      unknown: number;
    };
    totalWithDemographics: number;
  };
  locationStats?: {
    topCountries: Array<{
      country: string;
      countryCode?: string;
      count: number;
    }>;
    topCities: Array<{
      city: string;
      country: string;
      count: number;
    }>;
  };
  topPostsToday: Array<{
    id: string;
    text: string;
    likeCount: number;
    commentCount: number;
    authorUsername: string;
    createdAt: string;
  }>;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canUse = !!user?.isAdmin;

  useEffect(() => {
    if (canUse) {
      loadDashboardStats();
    }
  }, [canUse]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await adminDashboardStats();
      setStats(data.stats);
    } catch (err: any) {
      console.error('Dashboard stats hatası:', err);
      setError(err.message || 'İstatistikler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatGrowth = (growthRate: string) => {
    const rate = parseFloat(growthRate);
    const isPositive = rate >= 0;
    return (
      <span style={{ 
        color: isPositive ? '#10b981' : '#ef4444',
        fontSize: 13,
        fontWeight: 600,
        marginTop: 4,
        display: 'block'
      }}>
        {isPositive ? '↑' : '↓'} {Math.abs(rate).toFixed(1)}%
      </span>
    );
  };

  const getGenderLabel = (gender: string) => {
    const labels: Record<string, string> = {
      male: 'Erkek',
      female: 'Kadın',
      other: 'Diğer',
      'prefer-not-to-say': 'Belirtmem',
      unknown: 'Bilinmiyor',
    };
    return labels[gender] || gender;
  };

  if (!canUse) {
    return (
      <div style={{ padding: 24, color: 'var(--text)' }}>
        <h2>Admin Dashboard</h2>
        <p>Bu sayfa için admin yetkisi gerekli.</p>
      </div>
    );
  }

  if (loading && !stats) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '70vh',
        color: 'var(--text)'
      }}>
        <div className="spinner" style={{ marginBottom: 16 }} />
        <p style={{ color: 'var(--muted)' }}>Dashboard yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, maxWidth: 600, margin: '0 auto' }}>
        <div style={{
          background: 'var(--bg2)',
          border: '1px solid var(--danger)',
          borderRadius: 12,
          padding: 20,
          textAlign: 'center'
        }}>
          <h3 style={{ color: 'var(--danger)', marginBottom: 8 }}>⚠️ Hata</h3>
          <p style={{ color: 'var(--text)', marginBottom: 16 }}>{error}</p>
          <button onClick={loadDashboardStats} className="btn-primary">
            🔄 Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 24 
      }}>
        <h1 style={{ 
          fontSize: 32, 
          fontWeight: 'bold',
          color: 'var(--text)',
          margin: 0
        }}>
          📊 Admin Dashboard
        </h1>
        <button 
          onClick={loadDashboardStats}
          style={{
            padding: '8px 16px',
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 8,
            color: 'var(--text)',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          🔄 Yenile
        </button>
      </div>

      {/* Users Stats Section */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ 
          fontSize: 20, 
          fontWeight: 'bold',
          color: 'var(--text)',
          marginBottom: 16 
        }}>
          👥 Kullanıcı İstatistikleri
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            borderRadius: 12,
            padding: 20,
            color: '#fff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: 36, fontWeight: 'bold', marginBottom: 4 }}>
              {stats.users.total.toLocaleString()}
            </div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>Toplam Kullanıcı</div>
          </div>

          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.users.today}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Bugün Kayıt Olan</div>
            {formatGrowth(stats.users.growthRate)}
          </div>

          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.users.active24h}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Aktif Kullanıcı (24h)</div>
          </div>

          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.users.suspended}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Askıya Alınmış</div>
          </div>

          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.users.shadowBanned}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Shadowban</div>
          </div>
        </div>
      </section>

      {/* Posts Stats Section */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ 
          fontSize: 20, 
          fontWeight: 'bold',
          color: 'var(--text)',
          marginBottom: 16 
        }}>
          📝 Gönderi İstatistikleri
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #14532d 0%, #16a34a 100%)',
            borderRadius: 12,
            padding: 20,
            color: '#fff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: 36, fontWeight: 'bold', marginBottom: 4 }}>
              {stats.posts.total.toLocaleString()}
            </div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>Toplam Post</div>
          </div>

          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.posts.today}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Bugün Paylaşılan</div>
            {formatGrowth(stats.posts.growthRate)}
          </div>
        </div>
      </section>

      {/* Engagement Stats Section */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ 
          fontSize: 20, 
          fontWeight: 'bold',
          color: 'var(--text)',
          marginBottom: 16 
        }}>
          📊 Etkileşim Metrikleri
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
        }}>
          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.engagement.totalLikes.toLocaleString()}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Toplam Like</div>
          </div>

          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.engagement.totalComments.toLocaleString()}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Toplam Yorum</div>
          </div>

          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.engagement.avgLikesPerPost}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Ortalama Like/Post</div>
          </div>

          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 20,
          }}>
            <div style={{ fontSize: 32, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
              {stats.engagement.avgCommentsPerPost}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>Ortalama Yorum/Post</div>
          </div>
        </div>
      </section>

      {/* Demographics Section */}
      {stats.demographics && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ 
            fontSize: 20, 
            fontWeight: 'bold',
            color: 'var(--text)',
            marginBottom: 16 
          }}>
            👥 Demografi Dağılımı
          </h2>
          
          {/* Age Groups */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
              Yaş Grupları
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
            }}>
              {Object.entries(stats.demographics.ageGroups).map(([ageGroup, count]) => (
                <div
                  key={ageGroup}
                  style={{
                    background: 'var(--bg2)',
                    border: '1px solid var(--muted)',
                    borderRadius: 10,
                    padding: 16,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
                    {count}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {ageGroup === 'unknown' ? 'Bilinmiyor' : ageGroup}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gender Distribution */}
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
              Cinsiyet Dağılımı
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 12,
            }}>
              {Object.entries(stats.demographics.genderDistribution).map(([gender, count]) => (
                <div
                  key={gender}
                  style={{
                    background: 'var(--bg2)',
                    border: '1px solid var(--muted)',
                    borderRadius: 10,
                    padding: 16,
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: 24, fontWeight: 'bold', color: 'var(--text)', marginBottom: 4 }}>
                    {count}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {getGenderLabel(gender)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', textAlign: 'center' }}>
            Toplam demografik veri: {stats.demographics.totalWithDemographics} kullanıcı
          </p>
        </section>
      )}

      {/* Location Stats Section */}
      {stats.locationStats && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ 
            fontSize: 20, 
            fontWeight: 'bold',
            color: 'var(--text)',
            marginBottom: 16 
          }}>
            🌍 Lokasyon İstatistikleri
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Top Countries */}
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                En Çok Kullanıcı - Ülkeler
              </h3>
              {stats.locationStats.topCountries.slice(0, 5).map((country, index) => (
                <div
                  key={country.country}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'var(--bg2)',
                    border: '1px solid var(--muted)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      color: index === 0 ? '#fbbf24' : 'var(--muted)',
                      minWidth: 30
                    }}>
                      #{index + 1}
                    </span>
                    <span style={{ fontSize: 14, color: 'var(--text)' }}>
                      {country.countryCode ? `${country.countryCode} ` : ''}{country.country}
                    </span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>
                    {country.count}
                  </span>
                </div>
              ))}
            </div>

            {/* Top Cities */}
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
                En Çok Kullanıcı - Şehirler
              </h3>
              {stats.locationStats.topCities.slice(0, 5).map((city, index) => (
                <div
                  key={`${city.city}-${city.country}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: 'var(--bg2)',
                    border: '1px solid var(--muted)',
                    borderRadius: 8,
                    padding: '12px 16px',
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ 
                      fontSize: 16, 
                      fontWeight: 'bold', 
                      color: index === 0 ? '#fbbf24' : 'var(--muted)',
                      minWidth: 30
                    }}>
                      #{index + 1}
                    </span>
                    <span style={{ fontSize: 14, color: 'var(--text)' }}>
                      {city.city}, {city.country}
                    </span>
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>
                    {city.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top Posts Today Section */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ 
          fontSize: 20, 
          fontWeight: 'bold',
          color: 'var(--text)',
          marginBottom: 16 
        }}>
          🔥 Bugünün En Popüler Postları
        </h2>
        {stats.topPostsToday.length === 0 ? (
          <div style={{
            background: 'var(--bg2)',
            border: '1px solid var(--muted)',
            borderRadius: 12,
            padding: 32,
            textAlign: 'center',
            color: 'var(--muted)',
          }}>
            Bugün henüz post paylaşılmamış
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {stats.topPostsToday.map((post, index) => (
              <div
                key={post.id}
                style={{
                  background: 'var(--bg2)',
                  border: '1px solid var(--muted)',
                  borderRadius: 12,
                  padding: 16,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onClick={() => navigate(`/posts/${post.id}`)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                  <span style={{ 
                    fontSize: 18, 
                    fontWeight: 'bold', 
                    color: index === 0 ? '#fbbf24' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : 'var(--muted)',
                    minWidth: 30
                  }}>
                    #{index + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontSize: 14, 
                      fontWeight: 600, 
                      color: 'var(--primary)',
                      marginBottom: 6
                    }}>
                      @{post.authorUsername}
                    </div>
                    <p style={{ 
                      fontSize: 14, 
                      color: 'var(--text)', 
                      lineHeight: 1.5,
                      margin: 0,
                      marginBottom: 8,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}>
                      {post.text}
                    </p>
                    <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--muted)' }}>
                      <span>❤️ {post.likeCount.toLocaleString()}</span>
                      <span>💬 {post.commentCount.toLocaleString()}</span>
                      <span>🕒 {new Date(post.createdAt).toLocaleTimeString('tr-TR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Quick Actions Section */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ 
          fontSize: 20, 
          fontWeight: 'bold',
          color: 'var(--text)',
          marginBottom: 16 
        }}>
          ⚙️ Hızlı Erişim
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth < 600 ? '1fr' : window.innerWidth < 900 ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
        }}>
          <button
            onClick={() => navigate('/admin/users')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Kullanıcı Yönetimi
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/reports')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Raporlar
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/savwar')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>⚔️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              SavWar Yönetimi
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/security')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Güvenlik Dashboard
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/audit')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📜</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Audit Logs
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/outbox')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📤</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Outbox
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/ads')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--primary)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📺</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Reklam Ayarları
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/kabiles')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#ff9800';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>🏛️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Kabile Yönetimi
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/kabiles-mobile')}
            style={{
              background: 'var(--bg2)',
              border: '1px solid var(--muted)',
              borderRadius: 12,
              padding: 20,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#9c27b0';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--muted)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 8 }}>📱</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Kabile Yönetimi (Mobile)
            </div>
          </button>
        </div>
      </section>
    </div>
  );
}
